// Phase 38 follow-up: inline "shadow" quick-reply suggestions rendered directly
// above the composer. Surfaces the most relevant canned answers for the focused
// ticket's case type (SV / WL) so the agent can ONE-TAP insert a reply without
// opening the ⚡ picker. Tapping a chip INSERTS the answer body into the
// composer (append, never auto-send) — the same insert path as the picker.
//
// Selection rule (mirrors the picker's caseType matching in 38-03):
//   - suggestedCaseType set ('SV'|'WL') AND a category tagged with that caseType
//     exists → that category's answers.
//   - otherwise (organic ticket / no match) → General fallback: answers from
//     untagged (caseType == null) categories.
// Top N by the server's category/answer sort order. Renders NOTHING when there
// is nothing to suggest, so the composer collapses back to its normal layout.
//
// Self-loading like CannedResponsePicker — owns its useCannedResponses() and
// warms the same grouped picker list on mount. Visibility (hide-on-type etc.)
// is owned by the parent, which only mounts this when the composer is idle.

import { useEffect, useMemo } from 'react';
import { Zap } from 'lucide-react';
import { useCannedResponses } from '../../hooks/useCannedResponses';
import type { CaseType } from '../../types';

/** Cap so the strip stays a single scannable row, not a wall of chips. */
const MAX_SUGGESTIONS = 5;

export interface ShadowSuggestionsProps {
  /** Focused ticket's Phase 36 caseType ('SV'|'WL') or null (general/organic). */
  suggestedCaseType: CaseType | null;
  /** Insert the chosen answer body into the composer (append, no send). */
  onInsert: (body: string) => void;
  /**
   * Whether the strip is currently shown. Owned by the parent (hide-on-type,
   * no-attachment, not-replying/editing/recording). Kept as a prop rather than
   * unmounting so the one-time picker fetch survives draft typing — the
   * component stays mounted and just renders null while hidden.
   */
  visible: boolean;
}

export function ShadowSuggestions({ suggestedCaseType, onInsert, visible }: ShadowSuggestionsProps) {
  const { pickerCategories, loadPicker } = useCannedResponses();

  // Load the grouped picker list once on mount. Idempotent server-side and the
  // SAME list the ⚡ picker uses, so this just warms it for both surfaces.
  useEffect(() => {
    void loadPicker();
  }, [loadPicker]);

  const suggestions = useMemo(() => {
    if (pickerCategories.length === 0) return [];
    // Prefer categories whose caseType matches the ticket; fall back to the
    // untagged (General) categories when there is no case type / no match.
    const matched =
      suggestedCaseType != null
        ? pickerCategories.filter((c) => c.caseType === suggestedCaseType)
        : [];
    const source =
      matched.length > 0
        ? matched
        : pickerCategories.filter((c) => c.caseType == null);
    // Flatten to answers in category/sort order, cap at MAX. GET / only returns
    // active answers, but filter defensively in case that ever changes.
    return source
      .flatMap((c) => c.answers)
      .filter((a) => a.isActive)
      .slice(0, MAX_SUGGESTIONS);
  }, [pickerCategories, suggestedCaseType]);

  if (!visible || suggestions.length === 0) return null;

  return (
    // Vertical, right-aligned stack on the agent (backoffice) side — the agent
    // replies on the right, so suggested replies preview down that same side as
    // faint "ghost" outgoing bubbles (white + hairline border + shadow, with the
    // outgoing-bubble bottom-right tail), under a muted caption. Top 5 for the
    // matched category; tap one to drop its body into the composer.
    <div
      className="flex flex-col items-end gap-1.5 px-3 pt-2 pb-1.5"
      role="group"
      aria-label="Quick reply suggestions"
    >
      <div className="flex items-center gap-1 pr-1 mb-0.5 text-gray-400">
        <Zap className="w-3 h-3" strokeWidth={2.5} aria-hidden />
        <span className="text-[11px] font-semibold uppercase tracking-wide">Suggested replies</span>
      </div>
      {suggestions.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onInsert(a.body)}
          title={a.title}
          aria-label={`Insert quick reply: ${a.title}`}
          className="max-w-[85%] truncate px-3.5 py-2 rounded-2xl rounded-br-md text-[13px] leading-tight text-right text-gray-700 bg-white border border-gray-200 shadow-sm active:bg-gray-50 transition-colors touch-active"
        >
          {a.title}
        </button>
      ))}
    </div>
  );
}
